import { useEffect, useRef, useState, useCallback } from "react";
import type { Toast } from "./types";
import { removeToast } from "./store";

function ToastIcon({ type }: { type: string }) {
  switch (type) {
    case "success":
      return (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M24.75 9L11.75 22L5.25 15.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "error":
      return (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M23 8L8 23M8 8L23 23" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "warning":
    default:
      return (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M15.947 14.063c0-.249-.099-.487-.275-.663a.938.938 0 0 0-1.327 0 .937.937 0 0 0-.274.663v5.625c0 .248.098.487.274.663a.938.938 0 0 0 1.327 0 .937.937 0 0 0 .275-.663v-5.625Zm.47-3.75a1.407 1.407 0 1 1-2.814 0 1.407 1.407 0 0 1 2.813 0ZM15.01 1.875C11.527 1.875 8.186 3.258 5.723 5.72A13.125 13.125 0 0 0 1.876 15c0 3.481 1.384 6.82 3.847 9.281A13.14 13.14 0 0 0 15.01 28.125c3.484 0 6.825-1.383 9.288-3.844A13.125 13.125 0 0 0 28.146 15c0-3.481-1.384-6.82-3.847-9.281A13.14 13.14 0 0 0 15.01 1.875Zm-11.258 13.125a11.25 11.25 0 1 1 22.5 0 11.25 11.25 0 0 1-22.5 0Z" fill="currentColor" />
        </svg>
      );
  }
}

interface Props {
  toast: Toast;
}

export default function ToastItem({ toast: t }: Props) {
  const [exiting, setExiting] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const remainingRef = useRef(t.duration);
  const startedAtRef = useRef(Date.now());

  const startTimer = useCallback(() => {
    if (remainingRef.current <= 0) return;
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      setExiting(true);
    }, remainingRef.current);
  }, []);

  const pauseTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
  }, []);

  useEffect(() => {
    if (t.duration <= 0) return;
    startTimer();
    return () => clearTimeout(timerRef.current);
  }, [t.duration, t.id, startTimer]);

  const handleMouseEnter = () => {
    if (exiting) return;
    setPaused(true);
    pauseTimer();
  };

  const handleMouseLeave = () => {
    if (exiting) return;
    setPaused(false);
    startTimer();
  };

  const handleClose = () => {
    clearTimeout(timerRef.current);
    setExiting(true);
  };

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (exiting && e.animationName === "rd-toast-out") removeToast(t.id);
  };

  return (
    <div
      className={`rd-toast rd-toast--${t.type} ${exiting ? "rd-toast--exit" : "rd-toast--enter"}`}
      onAnimationEnd={handleAnimationEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="rd-toast__body">
        <span className="rd-toast__icon"><ToastIcon type={t.type} /></span>
        <div className="rd-toast__content">
          <div className="rd-toast__message">{t.message}</div>
          {t.description && <div className="rd-toast__description">{t.description}</div>}
        </div>
        <button type="button" className="rd-toast__close" onClick={handleClose}>
          <i className="fas fa-times" />
        </button>
      </div>
      <div
        className={`rd-toast__timer${paused ? " rd-toast__timer--paused" : ""}`}
        style={{ animationDuration: `${t.duration}ms` }}
      />
    </div>
  );
}
